import { useState } from "react";
import {
  CloseSvg,
  GenerateScheduleConfigIcon,
  InfoIcon,
} from "../../assets/icons/Icons";
import "./ScheduleView.scss";
import dayjs from "dayjs";
import OrderDropdown from "../Utils/OrderDropdown";
import { useDispatch, useSelector } from "react-redux";
import CustomTimePicker from "../../../CommonComponents/CustomTimePicker";
import {
  showErrorGenerateSchedulePopupConfig,
  setShowGenerateScheduleConfig,
  showScenarioErrorNotification,
} from "../../../../store/UnifiedControlPlaneSlice";
import UnifiedControlPlaneService from "../../UnifiedControlPlane.service";
import { showToastMessage } from "../../../../utils/commonFunctions";
import { isEmpty } from "lodash";

const GenerateScheduleConfig = ({ mission_id, scenarioId }) => {
  const dispatch = useDispatch();
  const currentUcpConfig = useSelector((state) => state.unifiedControlPlane.currentUcpConfig
  );
  const selectedScenario = useSelector(
    (state) => state.unifiedControlPlane.selectedScenario
  );
  const scenarioCurrentTime = useSelector(
    (state) => state.unifiedControlPlane.scenarioCurrentTime
  );

  const { current = [], pending = [] } = useSelector(
    (state) => state.unifiedControlPlane.orders || {}
  );

  const [selectedCurrent, setSelectedCurrent] = useState(() => (Array.isArray(current) ? current.map(order => order.orderId) : []));
  const [selectedPending, setSelectedPending] = useState(() => (Array.isArray(pending) ? pending.map(order => order.orderId) : []));

  const getScheduleName = () => {
    if (!selectedScenario || Object.keys(selectedScenario).length === 0) {
      return `Schedule_${dayjs.utc().format('YYYYMMDD_HHmmss')}`
    }
    if (selectedScenario?.status === "ReadyForSimulation") {
      return `Schedule_${dayjs.utc(selectedScenario.scenarioStartTime * 1000).format('YYYYMMDD_HHmmss')}`
    }
    if (selectedScenario?.status === "SimulationRunning") {
      return scenarioCurrentTime
        ? `Schedule_${dayjs.utc(scenarioCurrentTime).format('YYYYMMDD_HHmmss')}`
        : '';
    }

    return `Schedule_${dayjs.utc().format('YYYYMMDD_HHmmss')}`
  }

  const getScheduleStartTime = () => {
    const bufferMs = 120 * 60 * 1000;
    if (!selectedScenario || Object.keys(selectedScenario).length === 0) {
      return dayjs.utc().valueOf() + bufferMs
    }
    if (selectedScenario?.status === "ReadyForSimulation") {
      return dayjs.utc(selectedScenario.scenarioStartTime * 1000).valueOf() + bufferMs;
    }
    if (selectedScenario?.status === "SimulationRunning") {
      return dayjs.utc(scenarioCurrentTime).valueOf() + bufferMs;
    }

    return dayjs.utc().valueOf();
  }

  const [data, setData] = useState({
    scheduleName: getScheduleName(),
    startTime: getScheduleStartTime(),
    duration: "",
  });

  const [errors, setErrors] = useState({
    startTime: '',
    duration: '',
  });

  const validateSchedule = (newStartTime, newDuration) => {
    const scenarioStart = selectedScenario?.simulationStartTime * 1000;
    const simulationDuration = selectedScenario?.simulationDuration; // in minutes
    const scenarioEnd = scenarioStart + (simulationDuration * 60 * 1000);

    const startTime = newStartTime ?? data.startTime;
    const duration = parseInt(newDuration ?? data.duration);
    const endTime = startTime + duration * 60 * 1000;
    let valid = true;
    const newErrors = {
      startTime: '',
      duration: '',
    };

    // Format readable time strings
    const scenarioStartStr = dayjs(scenarioStart).utc().format("YYYY-MM-DD HH:mm:ss [UTC]");
    const scenarioEndStr = dayjs(scenarioEnd).utc().format("YYYY-MM-DD HH:mm:ss [UTC]");
    const selectedStartStr = dayjs(startTime).utc().format("YYYY-MM-DD HH:mm:ss [UTC]");

    // Skip if values are missing
    if (!startTime || isNaN(duration)) {
      setErrors(newErrors);
      return;
    }

    // Start time validation
    if (startTime < scenarioStart || startTime >= scenarioEnd) {
      newErrors.startTime = `Schedule start time (${selectedStartStr}) must be between scenario start (${scenarioStartStr}) and end (${scenarioEndStr}).`;
    }

    // Duration validation (only if start time is valid)
    if (!newErrors.startTime && endTime > scenarioEnd) {
      const maxDurationMins = Math.floor((scenarioEnd - startTime) / (60 * 1000));
      newErrors.duration = `Max duration from ${selectedStartStr} is ${maxDurationMins} mins (until ${scenarioEndStr}).`;
    }

    // Dispatch appropriate error message
    if (newErrors.startTime) {
      dispatch(showErrorGenerateSchedulePopupConfig(newErrors.startTime));
      valid = false;
    } else if (newErrors.duration) {
      dispatch(showErrorGenerateSchedulePopupConfig(newErrors.duration));
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const onDateChange = (date) => {
    const epoch = dayjs.utc(date).valueOf();
    setData(prev => ({ ...prev, startTime: epoch }));
    validateSchedule(epoch, data.duration);
  };

  const onDurationChange = (e) => {
    const duration = e.target.value;
    const validate = validateSchedule(data.startTime, duration);
    if (duration && !validate) return;
    setData(prev => ({ ...prev, duration }));
  };

  const scheduleUpdateSuccessCallback = () => {
    dispatch(UnifiedControlPlaneService.getAllSchedules(mission_id));
    dispatch(UnifiedControlPlaneService.getOrders());
  };
  const validateFields = () => {
    if (isEmpty(data.duration)) {
      showToastMessage('info', 'Please enter Duration');
      return false
    }
    else if (selectedPending?.length === 0 && selectedCurrent?.length === 0) {
      showToastMessage('info', 'Please select at least one Order');
      return false
    }
    return true
  }
  const GenerateSchedule = () => {
    const validated = validateFields();
    if (!validated) return;
    if (selectedScenario?.status === "SimulationCompleted") {
      dispatch(showScenarioErrorNotification("Current scenario is completed. Please select a different scenario"));
      dispatch(setShowGenerateScheduleConfig(false));
      return;
    }

    if (!currentUcpConfig || Object.keys(currentUcpConfig).length === 0) {
      dispatch(showScenarioErrorNotification("Please configure UCP by connecting a Scenario"));
      dispatch(setShowGenerateScheduleConfig(false));
      return;
    }

    const body = {
      label: data.scheduleName,
      startEpoch: Math.floor(data.startTime / 1000),
      duration: parseInt(data.duration),
      orders: [...selectedCurrent, ...selectedPending],
    };

    dispatch(UnifiedControlPlaneService.generateSchedule(scenarioId, mission_id, body, scheduleUpdateSuccessCallback));
    dispatch(setShowGenerateScheduleConfig(false));

  };

  const isValid = () => {
    return data.scheduleName.trim()
      && data.startTime
      && data.duration
      && !errors.startTime
      && !errors.duration
      && (selectedPending?.length || selectedCurrent?.length)
  };

  return (
    <div className="gs-config">
      <div className="gs-config-header">
        <div className="d-flex gap-1">
          <div className="gs-header-icon">
            <GenerateScheduleConfigIcon />
          </div>
          <div className="gs-header-text">Generate Schedule</div>
        </div>
        <div>
          <div
            className="gs-header-close-icon"
            onClick={() => dispatch(setShowGenerateScheduleConfig(false))}
          >
            <CloseSvg color={"#F0F0F0"} />
          </div>
        </div>
      </div>
      <div className="gs-config-body">
        <div className="gs-body-info">
          <div className="">
            <InfoIcon />
          </div>
          <div className="gs-body-info-text">
            Satellite details retrieved from TLE.
          </div>
        </div>
        <div className="gs-input-section">
          <div className="gs-input-title text-uppercase schedule-title">Schedule title</div>
          <input
            className="gs-input"
            placeholder="Schedule title"
            value={data.scheduleName}
            onChange={(e) => setData({ ...data, scheduleName: e.target.value })}
          />
        </div>
        <div className="gs-input-section">
          <div className="d-flex flex-row w-100 justify-space-between">
            <div className="gs-input-title gs-date-title">Start Time</div>
            <div className="gs-input-container gs-date-time-picker">
              <CustomTimePicker
                value={data.startTime}
                onChange={onDateChange}
              />
            </div>
          </div>
          <div className="d-flex flex-row w-100 justify-content-between">
            <div className="gs-input-title">Duration</div>
            <div className="gs-input-container d-flex flex-row">
              <input
                className="gs-input gs-input-duration"
                placeholder="Duration"
                type="number"
                value={data.duration}
                onChange={onDurationChange}
              />
              <span className="gs-input-unit">mins</span>
            </div>
          </div>
        </div>
        <div>
          <div className="gs-orders-header-container">
            <div className="gs-orders-header">Orders</div>
            <div className="gs-order-info">
              <div className="">
                <InfoIcon />
              </div>
              <div>
                By default, all orders are selected for schedule generation.
              </div>
            </div>
          </div>
          <div className="gs-dropdown-container">
            <div className="gs-dropdown-title">Current Orders</div>
            <OrderDropdown
              label="Current Orders"
              orders={current}
              selectedOrderIds={selectedCurrent}
              setSelectedOrderIds={setSelectedCurrent}
            />
          </div>
          <div className="gs-dropdown-container">
            <div className="gs-dropdown-title">Pending Orders</div>
            <OrderDropdown
              label="Pending Orders"
              orders={pending}
              selectedOrderIds={selectedPending}
              setSelectedOrderIds={setSelectedPending}
            />
          </div>
        </div>
      </div>
      <div className="gs-config-footer">
        <button
          className="gs-config-btns ps-btn-dark"
          onClick={() => dispatch(setShowGenerateScheduleConfig(false))}
        >
          Cancel
        </button>
        <button
          className={`gs-config-btns ps-btn-light ${!isValid() ? "disabled" : ""
            }`}
          disabled={!isValid()}
          onClick={GenerateSchedule}
        >
          Generate
        </button>
      </div>
    </div>
  );
};

export default GenerateScheduleConfig;
